import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import useDelayStore from '../store/delayStore';
import useAuthStore from '../store/authStore';
import useUIStore from '../store/uiStore';
import { formatDateDDMMYY } from '../utils/formatDate';

const statusLabels = {
  open: 'Open',
  escalated_7d: 'Escalated (7d)',
  escalated_15d: 'Escalated (15d)',
  closed: 'Closed',
};

const DelayDetailPage = () => {
  const { id } = useParams();
  const { selectedDelay, isLoading, isSubmitting, fetchDelay, closeDelay } = useDelayStore();
  const { user } = useAuthStore();
  const showToast = useUIStore((s) => s.showToast);

  const [closureSummary, setClosureSummary] = useState('');

  useEffect(() => {
    fetchDelay(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleClose = async (e) => {
    e.preventDefault();
    if (!closureSummary.trim()) {
      showToast('Closure summary is required', 'error');
      return;
    }
    const result = await closeDelay(id, {
      closed_by_id: user?.id,
      closure_summary: closureSummary,
    });
    if (result.success) {
      showToast('Delay closed', 'success');
      setClosureSummary('');
    } else {
      showToast(result.error, 'error');
    }
  };

  if (isLoading || !selectedDelay) {
    return <div className="min-h-screen p-6"><p className="text-gray-500">Loading...</p></div>;
  }

  const d = selectedDelay;

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-2xl mx-auto">
        <Link to="/delays" className="text-sm text-white hover:underline">&larr; Back to delays</Link>

        <div className="glass-card p-6 mt-3 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold text-gray-900">Delay #{d.id}</h1>
            <span className="px-2 py-1 rounded text-xs font-semibold bg-gray-100">
              {statusLabels[d.status] || d.status}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-gray-500">Punch Point</div>
              <div className="font-semibold">#{d.punch_point_id}</div>
            </div>
            <div>
              <div className="text-gray-500">Current Delay</div>
              <div className="font-semibold">
                {d.current_delay_days} day{d.current_delay_days === 1 ? '' : 's'}
                {d.status === 'closed' ? ' (final)' : ''}
              </div>
            </div>
            <div>
              <div className="text-gray-500">Expected Completion</div>
              <div className="font-semibold">{formatDateDDMMYY(d.expected_completion_date) || '—'}</div>
            </div>
            <div>
              <div className="text-gray-500">Actual Completion</div>
              <div className="font-semibold">{formatDateDDMMYY(d.actual_completion_date) || '—'}</div>
            </div>
            <div>
              <div className="text-gray-500">Responsible</div>
              <div className="font-semibold">
                {d.responsible_supervisor_name || d.responsible_dept_name || '—'}
                {d.is_external_cause ? ' (external cause)' : ''}
              </div>
            </div>
            <div>
              <div className="text-gray-500">Escalation Sent</div>
              <div className="font-semibold">{d.escalation_email_sent ? `Yes — ${formatDateDDMMYY(d.escalation_date)}` : 'No'}</div>
            </div>
          </div>

          {d.status === 'closed' && (
            <div className="mt-4 pt-4 border-t border-gray-100 text-sm">
              <div className="text-gray-500">Closure Summary</div>
              <div className="mt-1">{d.closure_summary}</div>
            </div>
          )}
        </div>

        <div className="glass-card mb-6">
          <div className="px-5 py-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">Escalation Notifications ({d.notifications?.length || 0})</h2>
          </div>
          {!d.notifications || d.notifications.length === 0 ? (
            <p className="text-gray-500 text-sm p-5">No escalation notifications logged yet.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {d.notifications.map((n) => (
                <li key={n.id} className="px-5 py-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{n.notified_role} — {n.notified_email}</span>
                    <span className="text-xs px-2 py-1 rounded bg-gray-100">{n.escalation_level}</span>
                  </div>
                  <div className="text-gray-500 text-xs mt-1">{n.message_summary}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {d.status !== 'closed' && (
          <form onSubmit={handleClose} className="glass-card p-6 space-y-3">
            <h2 className="font-semibold text-gray-900">Close Delay</h2>
            <textarea
              rows={3}
              value={closureSummary}
              onChange={(e) => setClosureSummary(e.target.value)}
              placeholder="Final summary..."
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60"
            >
              {isSubmitting ? 'Closing...' : 'Close Delay'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default DelayDetailPage;
